import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { X, CreditCard, Lock, ShieldCheck } from 'lucide-react';
import { checkoutSubscription } from '../../redux/subscriptionSlice';
import { BILLING_PERIODS } from '../../data/accessControls';

const onlyDigits = (s) => String(s || '').replace(/\D/g, '');
const formatCardNumber = (s) => onlyDigits(s).slice(0, 16).replace(/(.{4})/g, '$1 ').trim();
const formatExpiry = (s) => {
  const d = onlyDigits(s).slice(0, 4);
  return d.length >= 3 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};
const money = (p) => (p == null || String(p) === '' ? 'On request' : (String(p) === '0' ? 'Free' : `€${p}`));

// SIMULATED card checkout — looks like a real gateway but performs no real charge.
// On submit it records a paid payment and activates the plan for the chosen
// billing period (backend mock). A real provider replaces the pay() call later.
const SubscriptionCheckoutModal = ({ pkg, doctorId, onClose, onSuccess, initialPeriod = null, lockPeriod = false }) => {
  const dispatch = useDispatch();
  const checkingOut = useSelector((s) => s.subscription.checkingOut);
  const sub = useSelector((s) => s.subscription.mySubscription);
  const [card, setCard] = useState({ number: '', name: '', expiry: '', cvc: '' });
  const [period, setPeriod] = useState(null);

  // Enabled billing periods for this package (admin-configured). Empty = legacy
  // single price/duration.
  const bp = pkg?.billingPeriods || {};
  const periods = BILLING_PERIODS.filter((p) => bp[p.id] && bp[p.id].enabled)
    .map((p) => ({ ...p, price: bp[p.id].price }));
  const hasPeriods = periods.length > 0;

  // Default the period: an admin-set pending change pins it (lockPeriod), else the
  // first option.
  useEffect(() => {
    if (hasPeriods && !period) {
      const pinned = initialPeriod && periods.some((p) => p.id === initialPeriod) ? initialPeriod : periods[0].id;
      setPeriod(pinned);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pkg?.id]);

  const selected = periods.find((p) => p.id === period);
  const amount = hasPeriods ? selected?.price : pkg?.price;
  const durationDays = hasPeriods ? selected?.days : (pkg?.durationDays || 30);

  // Upgrade proration: credit the unused value of the current paid plan toward
  // this purchase. Mirrors the server (which recomputes + enforces it).
  const numAmount = parseFloat(amount);
  const isNumeric = Number.isFinite(numAmount);
  const remaining = Number(sub?.remainingValue) || 0;
  const credit = isNumeric ? Math.min(remaining, numAmount) : 0;
  const finalAmount = isNumeric ? Math.max(0, numAmount - credit) : 0;
  const payLabel = isNumeric ? `€${finalAmount.toLocaleString()}` : money(amount);

  const valid =
    (!hasPeriods || !!selected) &&
    onlyDigits(card.number).length >= 12 &&
    card.name.trim() &&
    onlyDigits(card.expiry).length === 4 &&
    onlyDigits(card.cvc).length >= 3;

  const pay = async () => {
    if (!valid) { toast.warn('Please complete the card details'); return; }
    try {
      await dispatch(checkoutSubscription({
        doctorId, packageId: pkg.id, card, period: hasPeriods ? period : undefined,
      })).unwrap();
      toast.success(`Payment successful — ${pkg.name} activated`);
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.warn(typeof e === 'string' ? e : 'Payment failed — please try again');
    }
  };

  const field = 'w-full px-3 py-2.5 rounded-xl border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] px-4" data-testid="checkout-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#4ABDBD]" />
            <h2 className="text-lg font-bold text-[#1a365d]">Pay for {pkg?.name}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Billing period selector (hidden when an admin pinned the period) */}
          {hasPeriods && !lockPeriod && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 mb-1.5">Billing period</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {periods.map((p) => {
                  const on = p.id === period;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPeriod(p.id)}
                      data-testid={`period-${p.id}`}
                      className={`text-left rounded-xl border px-3 py-2 transition-all ${on ? 'border-[#4ABDBD] ring-1 ring-[#4ABDBD] bg-[#4ABDBD]/5' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <div className="text-sm font-semibold text-gray-700">{p.label}</div>
                      <div className="text-xs text-slate-500">{money(p.price)} · {p.days} days</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Two columns on desktop: summary + notice | card form */}
          <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
            <div className="space-y-4">
              {/* Plan summary + upgrade credit */}
              <div className="bg-slate-50 rounded-xl px-4 py-3 space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600">{pkg?.name} · {durationDays} days</span>
                  <span className={credit > 0 ? 'text-slate-500 line-through' : 'font-semibold text-slate-700'}>{money(amount)}</span>
                </div>
                {credit > 0 && (
                  <div className="flex items-center justify-between text-sm text-emerald-600" data-testid="checkout-credit">
                    <span>Credit · {sub?.daysLeft} day{sub?.daysLeft === 1 ? '' : 's'} left on {sub?.packageName}</span>
                    <span>−€{credit.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-1.5 border-t border-slate-200">
                  <span className="text-sm font-semibold text-[#1a365d]">Total due</span>
                  <span className="text-lg font-bold text-[#1a365d]" data-testid="checkout-total">{payLabel}</span>
                </div>
              </div>

              {/* Simulated-payment notice */}
              <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Demo checkout — this records the payment and activates your plan, but <strong>no real charge</strong> is made.</span>
              </div>
            </div>

            {/* Card form */}
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Card number</label>
                <input data-testid="card-number" inputMode="numeric" placeholder="4242 4242 4242 4242"
                  value={card.number} onChange={(e) => setCard({ ...card, number: formatCardNumber(e.target.value) })} className={field} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1">Name on card</label>
                <input data-testid="card-name" placeholder="Dr. Jane Doe"
                  value={card.name} onChange={(e) => setCard({ ...card, name: e.target.value })} className={field} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Expiry</label>
                  <input data-testid="card-expiry" inputMode="numeric" placeholder="MM/YY"
                    value={card.expiry} onChange={(e) => setCard({ ...card, expiry: formatExpiry(e.target.value) })} className={field} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">CVC</label>
                  <input data-testid="card-cvc" inputMode="numeric" placeholder="123"
                    value={card.cvc} onChange={(e) => setCard({ ...card, cvc: onlyDigits(e.target.value).slice(0, 4) })} className={field} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="p-4 sm:p-6 border-t border-gray-200">
          <button type="button" onClick={pay} disabled={checkingOut} data-testid="checkout-pay"
            className={`w-full px-4 py-3 rounded-xl text-white font-semibold text-sm transition-all flex items-center justify-center gap-2 ${checkingOut ? 'bg-gray-300 cursor-not-allowed' : 'bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] hover:shadow-lg'}`}>
            {checkingOut ? (
              <><span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing…</>
            ) : (
              <><Lock className="w-4 h-4" /> Pay {payLabel}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckoutModal;
